import { FC, useMemo, useRef, useState } from "react";
import {
  DataGridPro as DataGrid,
  GridColDef,
  GridFilterModel,
  GridLoadingOverlay,
  GridLogicOperator,
  GridRowParams,
  GridSortModel,
} from "@mui/x-data-grid-pro";
import { Alert, Box, Paper } from "@mui/material";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import { format } from "date-fns";
import { pl } from "date-fns/locale/pl";
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import __ from "src/utils/Translations";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import { useTableOperators } from "src/utils/hooks/absences/useTableOperators";
import { useFetchSyncErrors } from "src/Pages/Company/OrganizationStructure/api/useFetchSyncErrors";
import { SyncError } from "src/Pages/Company/EmployeeManagement/SyncErrors/models/syncErrors.models";
import { buildRsqlFilters } from "src/Pages/Company/OrganizationStructure/utils/buildRsqlFilters";
import { TableSettings } from "./tables.models";
import DateRangeFilter from "./DateRangeFilter";
import GridLinkAction from "./GridLinkAction";
import SyncErrorsToolbar from "./SyncErrorsToolbar";

const SyncErrorsTable: FC = () => {
  const companyId = useCompanyId();
  const { getStringOperators, getDateOperators } = useTableOperators();

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
    logicOperator: GridLogicOperator.And,
  });

  const handleSortModelChange = (newSortModel: GridSortModel) => {
    setSortModel(newSortModel);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleFilterModelChange = (newFilterModel: GridFilterModel) => {
    setFilterModel(newFilterModel);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };
  const [settings, setSettings] = useState<TableSettings>({
    density: "standard",
  });
  const [showFilters, setShowFilters] = useState(true);

  const requestParams = useMemo(
    () => ({
      companyId,
      page: paginationModel.page,
      size: paginationModel.pageSize,
      sort:
        sortModel.map(({ field, sort }) => `${field}:${sort}`).join(",") ||
        undefined,
      filters: buildRsqlFilters(filterModel, ["issuedAt"]),
    }),
    [
      companyId,
      paginationModel.page,
      paginationModel.pageSize,
      sortModel,
      filterModel,
    ]
  );

  const { data, isFetching, isError, error } =
    useFetchSyncErrors(requestParams);

  const rowCountRef = useRef(data?.totalElements ?? 0);
  const rowCount = useMemo(() => {
    if (data?.totalElements !== undefined) {
      rowCountRef.current = data.totalElements;
    }
    return rowCountRef.current;
  }, [data?.totalElements]);

  const columns: GridColDef<SyncError>[] = useMemo(
    () => [
      {
        field: "issuedAt",
        headerName: __("Data żądania"),
        width: 256,
        renderHeaderFilter: (params) => <DateRangeFilter {...params} />,
        valueFormatter: (value: string) =>
          value ? format(new Date(value), "dd.MM.yyyy, HH:mm:ss") : "-",
      },
      {
        field: "firstName",
        headerName: __("Imię"),
        width: 256,
        filterOperators: getStringOperators({ labelUT: "Podaj imię" }),
        valueFormatter: (value: string | null) => value ?? "-",
      },
      {
        field: "lastName",
        headerName: __("Nazwisko"),
        width: 256,
        filterOperators: getStringOperators({ labelUT: "Podaj nazwisko" }),
        valueFormatter: (value: string | null) => value ?? "-",
      },
      {
        field: "fk",
        headerName: __("Numer kadrowo-płacowy"),
        width: 256,
        filterOperators: getStringOperators({
          labelUT: "Podaj numer kadrowo-płacowy",
        }),
      },
      {
        field: "errorCount",
        headerName: __("Liczba alertów"),
        width: 256,
        type: "number",
        align: "left",
        headerAlign: "left",
        filterable: false,
      },
      {
        field: "actions",
        type: "actions",
        headerName: __("Akcja"),
        width: 240,
        getActions: (params: GridRowParams<SyncError>) => [
          <GridLinkAction
            key="details"
            label={__("Szczegóły błędów")}
            to={{
              pathname: `sync-errors/${params.row.fk}`,
              state: {
                firstName: params.row.firstName,
                lastName: params.row.lastName,
                fk: params.row.fk,
                issuedAt: params.row.issuedAt,
              },
            }}
            icon={<ManageSearchIcon />}
            bgColor="primary.light"
            bgColorHover="primary.main"
            dataTestId="sync-error-details-action"
          />,
        ],
      },
    ],
    [getStringOperators]
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={pl}>
      <Paper data-testid="sync-errors-table" sx={{ pt: 2, pb: 1, mb: 2 }}>
        <Box sx={{ height: 770, width: "100%" }}>
          {isError && (
            <Alert variant="outlined" color="warning">
              {String(error)}
            </Alert>
          )}
          <DataGrid
            columns={columns}
            rows={data?.content ?? []}
            getRowId={(row) => `${row.fk}-${row.issuedAt}`}
            rowCount={rowCount}
            loading={isFetching}
            disableColumnMenu
            disableColumnSelector
            disableDensitySelector
            disableRowSelectionOnClick
            disableEval
            showCellVerticalBorder={false}
            showColumnVerticalBorder={false}
            density={settings.density}
            pagination
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[5, 10, 25, 50]}
            sortingMode="server"
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            disableColumnFilter
            headerFilters={showFilters}
            filterMode="server"
            filterModel={filterModel}
            onFilterModelChange={handleFilterModelChange}
            filterDebounceMs={500}
            showToolbar
            slots={{
              headerFilterMenu: null,
              toolbar: () => (
                <SyncErrorsToolbar
                  settings={settings}
                  setSettings={setSettings}
                  showFilters={showFilters}
                  setShowFilters={setShowFilters}
                />
              ),
              loadingOverlay: () => (
                <GridLoadingOverlay
                  variant="linear-progress"
                  noRowsVariant="skeleton"
                />
              ),
            }}
            slotProps={{
              row: { "data-testid": "sync-errors-table__row" },
              cell: { "data-testid": "sync-errors-table__cell" },
            }}
            localeText={{
              noRowsLabel: __("Brak błędów synchronizacji"),
            }}
          />
        </Box>
      </Paper>
    </LocalizationProvider>
  );
};

export default SyncErrorsTable;
